import { KeyboardEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Barcode, ChevronRight, Plus, Search } from 'lucide-react';
import { ItemTile } from '@/components/pos/ItemTile.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { listInventoryItems, scanBarcode, type InventoryItemListRow } from '@/lib/api/inventory.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import type { PosCategoryTab, StoreSettings } from '@/lib/api/stores.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError } from '@/lib/toast.js';
import {
  createCustomCartItem,
  createInventoryCartItem,
  createRepairCartItem,
  createTradeInCartItem,
  usePosCartStore,
} from '@/stores/pos-cart.js';
import { usePosUiStore } from '@/stores/pos-ui.js';

const CATEGORY_LABELS: Record<PosCategoryTab | 'all', string> = {
  all: 'All',
  repairs: 'Repairs',
  accessories: 'Accessories',
  unlocking: 'Unlocking',
  trade_in: 'Trade-In',
  misc: 'Misc',
};

const MAKE_ICONS: Record<string, string> = {
  apple: '🍎',
  samsung: '📱',
  google: '📱',
  motorola: '📱',
  laptop: '💻',
  console: '🎮',
};

function makeIcon(name: string) {
  const key = name.toLowerCase();
  if (key.includes('apple')) return MAKE_ICONS.apple;
  if (key.includes('laptop')) return MAKE_ICONS.laptop;
  if (key.includes('console')) return MAKE_ICONS.console;
  return '📱';
}

function servicePrice(service: RepairService) {
  return Number(service.resolvedPrice ?? service.defaultPrice ?? 0);
}

type PosItemSelectionProps = {
  store: StoreSettings;
};

export function PosItemSelection({ store }: PosItemSelectionProps) {
  const [scanInput, setScanInput] = useState('');
  const [accessorySearch, setAccessorySearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customQty, setCustomQty] = useState('1');
  const [tradeMakeId, setTradeMakeId] = useState('');
  const [tradeModelSearch, setTradeModelSearch] = useState('');
  const [tradeModelId, setTradeModelId] = useState('');
  const [tradeCondition, setTradeCondition] = useState('good');
  const [tradePrice, setTradePrice] = useState('');
  const [tradeNotes, setTradeNotes] = useState('');

  const addItem = usePosCartStore((s) => s.addItem);
  const setRepairSelection = usePosCartStore((s) => s.setRepairSelection);

  const activeCategory = usePosUiStore((s) => s.activeCategory);
  const repairStep = usePosUiStore((s) => s.repairStep);
  const selectedMake = usePosUiStore((s) => s.selectedMake);
  const selectedModel = usePosUiStore((s) => s.selectedModel);
  const setActiveCategory = usePosUiStore((s) => s.setActiveCategory);
  const selectMake = usePosUiStore((s) => s.selectMake);
  const selectModel = usePosUiStore((s) => s.selectModel);
  const resetRepairsNav = usePosUiStore((s) => s.resetRepairsNav);
  const goToRepairMakes = usePosUiStore((s) => s.goToRepairMakes);

  const enabledCategories = (store.posCategoriesEnabled ?? [
    'repairs',
    'accessories',
    'unlocking',
    'trade_in',
    'misc',
  ]) as PosCategoryTab[];

  const tabs: Array<PosCategoryTab | 'all'> = ['all', ...enabledCategories];
  const showOutOfStock = store.posShowOutOfStock !== false;
  const pinnedIds = store.posPinnedItems ?? [];

  const searchQuery = useQuery({
    queryKey: ['pos-scan', scanInput],
    queryFn: () => listInventoryItems({ search: scanInput, page: 1, pageSize: 8, sellableOnly: true }),
    enabled: scanInput.trim().length >= 2,
  });

  const pinnedQuery = useQuery({
    queryKey: ['pos-pinned', pinnedIds.join(',')],
    queryFn: async () => {
      if (!pinnedIds.length) return [];
      const res = await listInventoryItems({ page: 1, pageSize: 50, sellableOnly: true });
      return res.data.filter((item) => pinnedIds.includes(item.id));
    },
    enabled: pinnedIds.length > 0,
  });

  const recentQuery = useQuery({
    queryKey: ['pos-recent-items'],
    queryFn: () =>
      listInventoryItems({ page: 1, pageSize: 12, sort: 'lastReceived', sellableOnly: true }),
  });

  const makesQuery = useQuery({
    queryKey: ['pos-makes'],
    queryFn: listDeviceMakes,
    enabled: activeCategory === 'repairs' && repairStep !== 'services',
  });

  const modelsQuery = useQuery({
    queryKey: ['pos-models', selectedMake?.id, modelSearch],
    queryFn: () =>
      listDeviceModels({ makeId: selectedMake!.id, search: modelSearch || undefined }),
    enabled: activeCategory === 'repairs' && repairStep === 'models' && Boolean(selectedMake?.id),
  });

  const repairServicesQuery = useQuery({
    queryKey: ['pos-repair-services', selectedModel?.id],
    queryFn: () =>
      listRepairServices({
        modelId: selectedModel!.id,
        displayOnPos: true,
        requireModelPricing: true,
      }),
    enabled: activeCategory === 'repairs' && repairStep === 'services' && Boolean(selectedModel?.id),
  });

  const accessoriesQuery = useQuery({
    queryKey: ['pos-accessories', accessorySearch],
    queryFn: () =>
      listInventoryItems({
        category: 'retail_accessory',
        search: accessorySearch || undefined,
        page: 1,
        pageSize: 48,
        sellableOnly: true,
      }),
    enabled: activeCategory === 'accessories',
  });

  const miscQuery = useQuery({
    queryKey: ['pos-misc', accessorySearch],
    queryFn: () =>
      listInventoryItems({
        category: 'refurbished',
        search: accessorySearch || undefined,
        page: 1,
        pageSize: 48,
        sellableOnly: true,
      }),
    enabled: activeCategory === 'misc',
  });

  const unlockingQuery = useQuery({
    queryKey: ['pos-unlocking'],
    queryFn: () =>
      listRepairServices({ categorySlug: 'unlocking', displayOnPos: true }),
    enabled: activeCategory === 'unlocking',
  });

  const tradeMakesQuery = useQuery({
    queryKey: ['pos-trade-makes'],
    queryFn: listDeviceMakes,
    enabled: activeCategory === 'trade_in',
  });

  const tradeModelsQuery = useQuery({
    queryKey: ['pos-trade-models', tradeMakeId, tradeModelSearch],
    queryFn: () =>
      listDeviceModels({ makeId: tradeMakeId, search: tradeModelSearch || undefined }),
    enabled: activeCategory === 'trade_in' && Boolean(tradeMakeId),
  });

  const orderedPinned = useMemo(() => {
    const map = new Map((pinnedQuery.data ?? []).map((item) => [item.id, item]));
    return pinnedIds.map((id) => map.get(id)).filter(Boolean) as InventoryItemListRow[];
  }, [pinnedIds, pinnedQuery.data]);

  function addInventoryItem(item: InventoryItemListRow) {
    const qty = Number(item.qtyOnHand ?? 0);
    if (qty <= 0) return;
    addItem(
      createInventoryCartItem({
        inventoryItemId: item.id,
        name: item.name,
        price: Number(item.retailPrice ?? 0),
        sku: item.barcodeInternal ?? undefined,
        qtyOnHand: qty,
        taxable: true,
      }),
    );
  }

  function addRepairService(service: RepairService) {
    if (!selectedMake || !selectedModel) return;
    addItem(
      createRepairCartItem({
        serviceId: service.id,
        serviceName: service.name,
        price: servicePrice(service),
        makeId: selectedMake.id,
        makeName: selectedMake.name,
        modelId: selectedModel.id,
        modelName: selectedModel.name,
        estimatedMinutes: service.resolvedEstimatedMinutes ?? service.estimatedMinutes,
        warrantyDays: service.resolvedWarrantyDays ?? service.defaultWarrantyDays,
        warrantyType: service.resolvedWarrantyType ?? service.defaultWarrantyType,
      }),
    );
    setRepairSelection({
      makeId: selectedMake.id,
      makeName: selectedMake.name,
      modelId: selectedModel.id,
      modelName: selectedModel.name,
    });
  }

  async function handleScanKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const code = scanInput.trim();
    if (!code) return;
    try {
      const result = await scanBarcode(code);
      if (result.type === 'item') {
        addInventoryItem(result.data as unknown as InventoryItemListRow);
        setScanInput('');
        return;
      }
    } catch {
      const first = searchQuery.data?.data[0];
      if (first) {
        addInventoryItem(first);
        setScanInput('');
        return;
      }
      toastError(parseApiError(new Error('No matching item found')).message);
    }
  }

  function renderInventoryGrid(items: InventoryItemListRow[]) {
    const visible = showOutOfStock
      ? items
      : items.filter((item) => Number(item.qtyOnHand ?? 0) > 0);

    return (
      <div className="grid grid-cols-3 gap-2">
        {visible.map((item) => {
          const stock = Number(item.qtyOnHand ?? 0);
          const outOfStock = stock <= 0;
          return (
            <ItemTile
              key={item.id}
              title={item.name}
              subtitle={`Stock: ${stock}`}
              price={item.retailPrice}
              badge={outOfStock ? 'Out of Stock' : null}
              disabled={outOfStock}
              onClick={() => addInventoryItem(item)}
            />
          );
        })}
      </div>
    );
  }

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden border-r border-slate-200 bg-slate-50">
      <div className="shrink-0 border-b border-slate-200 bg-white p-3">
        <div className="relative">
          <Barcode className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <input
            autoFocus
            className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary"
            placeholder="Scan or search items, services, SKUs..."
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            onKeyDown={handleScanKey}
          />
          {searchQuery.data?.data.length ? (
            <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {searchQuery.data.data.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="flex w-full justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => {
                    addInventoryItem(item);
                    setScanInput('');
                  }}
                >
                  <span>{item.name}</span>
                  <span className="font-semibold">{formatCurrency(item.retailPrice)}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-white px-3 py-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveCategory(tab)}
            className={[
              'shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold capitalize',
              activeCategory === tab
                ? 'bg-primary text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
            ].join(' ')}
          >
            {CATEGORY_LABELS[tab]}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
        {activeCategory === 'all' ? (
          <div className="space-y-4">
            {orderedPinned.length ? (
              <div>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Pinned
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {orderedPinned.map((item) => (
                    <ItemTile
                      key={item.id}
                      title={item.name}
                      price={item.retailPrice}
                      icon="⭐"
                      onClick={() => addInventoryItem(item)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                Recent
              </h3>
              {renderInventoryGrid(recentQuery.data?.data ?? [])}
            </div>
          </div>
        ) : null}

        {activeCategory === 'repairs' ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-1 text-xs font-semibold text-slate-600">
              <button type="button" onClick={goToRepairMakes} className="hover:text-primary">
                Repairs
              </button>
              {selectedMake ? (
                <>
                  <ChevronRight className="h-3 w-3" />
                  <button type="button" onClick={resetRepairsNav} className="hover:text-primary">
                    {selectedMake.name}
                  </button>
                </>
              ) : null}
              {selectedModel ? (
                <>
                  <ChevronRight className="h-3 w-3" />
                  <span className="text-slate-900">{selectedModel.name}</span>
                </>
              ) : null}
            </div>

            {repairStep === 'makes' ? (
              <div className="grid grid-cols-3 gap-2">
                {(makesQuery.data ?? []).map((make) => (
                  <ItemTile
                    key={make.id}
                    title={make.name}
                    icon={makeIcon(make.name)}
                    onClick={() => selectMake({ id: make.id, name: make.name })}
                  />
                ))}
              </div>
            ) : null}

            {repairStep === 'models' && selectedMake ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm"
                    placeholder={`Search ${selectedMake.name} models...`}
                    value={modelSearch}
                    onChange={(e) => setModelSearch(e.target.value)}
                  />
                </div>
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                  {(modelsQuery.data ?? []).map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-slate-50"
                      onClick={() => selectModel({ id: model.id, name: model.name })}
                    >
                      <span className="font-medium">{model.name}</span>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {repairStep === 'services' && selectedModel ? (
              <div className="space-y-2">
                {(repairServicesQuery.data ?? []).map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-950">{service.name}</div>
                      <div className="text-xs text-slate-500">
                        Est. {service.resolvedEstimatedMinutes ?? service.estimatedMinutes ?? '—'}{' '}
                        min ·{' '}
                        {service.resolvedWarrantyType === 'lifetime'
                          ? 'Lifetime warranty'
                          : service.resolvedWarrantyType === 'none'
                            ? 'No warranty'
                            : `${service.resolvedWarrantyDays ?? service.defaultWarrantyDays ?? '—'} day warranty`}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-bold">{formatCurrency(servicePrice(service))}</span>
                      <button
                        type="button"
                        onClick={() => addRepairService(service)}
                        className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ))}
                {!repairServicesQuery.isLoading && !(repairServicesQuery.data ?? []).length ? (
                  <p className="py-8 text-center text-sm text-slate-500">
                    No POS services priced for this model.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {activeCategory === 'accessories' || activeCategory === 'misc' ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm"
                placeholder={
                  activeCategory === 'accessories' ? 'Search accessories...' : 'Search misc items...'
                }
                value={accessorySearch}
                onChange={(e) => setAccessorySearch(e.target.value)}
              />
            </div>
            {renderInventoryGrid(
              (activeCategory === 'accessories'
                ? accessoriesQuery.data?.data
                : miscQuery.data?.data) ?? [],
            )}
            {activeCategory === 'misc' ? (
              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-3">
                <h4 className="mb-2 text-sm font-bold">Custom Item</h4>
                <input
                  className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Description"
                  value={customDesc}
                  onChange={(e) => setCustomDesc(e.target.value)}
                />
                <div className="mb-2 flex gap-2">
                  <input
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Price"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                  />
                  <input
                    className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    type="number"
                    min="1"
                    placeholder="Qty"
                    value={customQty}
                    onChange={(e) => setCustomQty(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  disabled={!customDesc.trim() || !Number(customPrice)}
                  onClick={() => {
                    addItem(
                      createCustomCartItem(
                        customDesc.trim(),
                        Number(customPrice),
                        Math.max(1, Number(customQty) || 1),
                      ),
                    );
                    setCustomDesc('');
                    setCustomPrice('');
                    setCustomQty('1');
                  }}
                  className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                >
                  <Plus className="h-3 w-3" />
                  Add to Cart
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {activeCategory === 'unlocking' ? (
          <div className="space-y-2">
            {(unlockingQuery.data ?? []).map((service) => (
              <div
                key={service.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3"
              >
                <div className="font-semibold text-slate-950">{service.name}</div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(servicePrice(service))}</span>
                  <button
                    type="button"
                    onClick={() =>
                      addItem(
                        createRepairCartItem({
                          serviceId: service.id,
                          serviceName: service.name,
                          price: servicePrice(service),
                          estimatedMinutes: service.estimatedMinutes,
                          warrantyDays: service.defaultWarrantyDays,
                          warrantyType: service.defaultWarrantyType,
                        }),
                      )
                    }
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white"
                  >
                    Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {activeCategory === 'trade_in' ? (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="font-bold text-slate-950">Start a Trade-In</h3>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={tradeMakeId}
              onChange={(e) => {
                setTradeMakeId(e.target.value);
                setTradeModelId('');
              }}
            >
              <option value="">Make</option>
              {(tradeMakesQuery.data ?? []).map((make) => (
                <option key={make.id} value={make.id}>
                  {make.name}
                </option>
              ))}
            </select>
            {tradeMakeId ? (
              <>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Search model..."
                  value={tradeModelSearch}
                  onChange={(e) => setTradeModelSearch(e.target.value)}
                />
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={tradeModelId}
                  onChange={(e) => setTradeModelId(e.target.value)}
                >
                  <option value="">Model</option>
                  {(tradeModelsQuery.data ?? []).map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            <div className="flex flex-wrap gap-1">
              {(['excellent', 'good', 'fair', 'poor', 'broken'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setTradeCondition(c)}
                  className={[
                    'rounded-lg px-2 py-1 text-xs font-bold capitalize',
                    tradeCondition === c ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600',
                  ].join(' ')}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              type="number"
              min="0"
              step="0.01"
              placeholder="Offered price"
              value={tradePrice}
              onChange={(e) => setTradePrice(e.target.value)}
            />
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Notes (optional)"
              value={tradeNotes}
              onChange={(e) => setTradeNotes(e.target.value)}
            />
            <button
              type="button"
              disabled={!tradeModelId || !Number(tradePrice)}
              onClick={() => {
                const make = (tradeMakesQuery.data ?? []).find((m) => m.id === tradeMakeId);
                const model = (tradeModelsQuery.data ?? []).find((m) => m.id === tradeModelId);
                const label = [make?.name, model?.name].filter(Boolean).join(' ');
                addItem(
                  createTradeInCartItem({
                    deviceDescription: label || 'Trade-In Device',
                    condition: tradeCondition,
                    offeredPrice: Number(tradePrice),
                  }),
                );
              }}
              className="w-full rounded-lg bg-primary py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              Add Trade-In to Cart
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
